import { Badge, Box, Flex, Heading, HStack, Text, useDisclosure } from '@chakra-ui/react';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { ProductFilterState, SortingState } from '../hooks/useProductListing.tsx';
import { ProductList } from './ProductList.tsx';
import { ProductViewModal } from '../productView/ProductViewModal.tsx';

type Props = {
  filterState: ProductFilterState;
  setFormIds: (value: number[]) => void;
  sidebar?: ReactNode;
};

export const ProductCatalogue = (props: Props) => {
  const [sortingState] = useState<SortingState>({ orderBy: 'name', isDesc: false });
  const [totalRows, setTotalRows] = useState(0);

  const disclosure = useDisclosure();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const handleProductViewOpen = useCallback(
    (productId: number) => {
      setSelectedProductId(productId);
      disclosure.onOpen();
    },
    [disclosure.onOpen]
  );

  const isFiltered = useMemo(() => {
    return !!(props.filterState.keyword || props.filterState.formIds?.length);
  }, [props.filterState.keyword, props.filterState.formIds]);

  return (
    <Box maxW={'1400px'} mx='auto'>
      {/* Header row - spans full width */}
      <HStack gap={{ base: '2', md: '3' }} mb={{ base: '3', md: '6' }}>
        <Heading size={{ base: 'md', md: 'lg' }}>Products</Heading>
        {isFiltered && (
          <Badge colorPalette='blue' variant='solid' size='sm'>
            Filtered
          </Badge>
        )}
        <Text color='gray.500' fontSize={{ base: 'xs', md: 'sm' }}>
          {totalRows} {isFiltered ? 'results' : 'items'}
        </Text>
      </HStack>

      {/* Content row - two columns on desktop when sidebar provided */}
      <Flex gap='6' alignItems='flex-start'>
        {/* Product list column */}
        <Box flex='1' minW='0'>
          <ProductList
            filterState={props.filterState}
            sortingState={sortingState}
            onProductViewOpen={handleProductViewOpen}
            onTotalRowsChange={setTotalRows}
          />
        </Box>

        {/* Sidebar column - sticky */}
        {props.sidebar && (
          <Box
            display={{ base: 'none', lg: 'block' }}
            width='320px'
            flexShrink={0}
            position='sticky'
            top='calc(4.5rem + 1.5rem)'
            alignSelf='flex-start'>
            {props.sidebar}
          </Box>
        )}
      </Flex>

      {selectedProductId && <ProductViewModal productId={selectedProductId} disclosure={disclosure} />}
    </Box>
  );
};
